/**
 * Upload Files Action
 * Triggers file upload to a folder
 *
 * Note: This action needs UI support - it triggers a file input
 * that the parent component should handle.
 */

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext, getFilePath } from "./types";
import { isReadOnlyDatum } from "../specialFolders";

/**
 * Callback type for triggering file upload
 */
export type TriggerUploadCallback = (
  targetPath: string[],
  driveId: string | null,
  uploadType: "files" | "folder"
) => void;

/**
 * Trigger file upload to a folder
 */
export async function uploadFiles(
  datum: Datum,
  context: ActionContext & { onTriggerUpload?: TriggerUploadCallback }
): Promise<void> {
  if (datum.type !== "folder") {
    context.showToast?.("Can only upload to folders", "error");
    return;
  }

  const targetPath = getFilePath(datum);
  const driveId = datum.payload?.driveId || context.driveId || null;

  // Call the callback if provided
  if (context.onTriggerUpload) {
    context.onTriggerUpload(targetPath, driveId, "files");
  } else {
    // Fallback: show a toast that upload isn't supported yet
    context.showToast?.(
      "File upload requires UI setup in parent component",
      "info"
    );
  }
}

/**
 * Trigger folder upload to a folder
 */
export async function uploadFolder(
  datum: Datum,
  context: ActionContext & { onTriggerUpload?: TriggerUploadCallback }
): Promise<void> {
  if (datum.type !== "folder") {
    context.showToast?.("Can only upload to folders", "error");
    return;
  }

  const targetPath = getFilePath(datum);
  const driveId = datum.payload?.driveId || context.driveId || null;

  // Call the callback if provided
  if (context.onTriggerUpload) {
    context.onTriggerUpload(targetPath, driveId, "folder");
  } else {
    // Fallback: show a toast that upload isn't supported yet
    context.showToast?.(
      "Folder upload requires UI setup in parent component",
      "info"
    );
  }
}

/**
 * Action definition for file upload
 */
export const uploadFilesAction: ActionDefinition = {
  config: {
    action: "upload-files",
    label: "Upload Files",
  },
  isApplicable: (datum, context) =>
    datum.type === "folder" && !isReadOnlyDatum(datum, context),
  execute: uploadFiles,
};

/**
 * Action definition for folder upload
 */
export const uploadFolderAction: ActionDefinition = {
  config: {
    action: "upload-folder",
    label: "Upload Folder",
  },
  isApplicable: (datum, context) =>
    datum.type === "folder" && !isReadOnlyDatum(datum, context),
  execute: uploadFolder,
};
